import React, { useEffect, useState } from "react";
import { Modal, DatePicker, Table, Space, Alert, message } from "antd";
import dayjs from "dayjs";
import ExportCsvButton from "./export-csv-button";

/**
 * Báo cáo chi trả theo tháng để gửi cơ quan thuế: mỗi tháng bao nhiêu user nhận
 * tiền và tổng tiền. Dùng chung cho rút tiền và đổi voucher — mỗi nơi truyền API
 * + nhãn cột riêng. Backend: controllers/monthly-tax-report-controller.js.
 *
 * Props:
 *  - title, filePrefix, note
 *  - fetchSummary(params) → axios response { data: { data: { months, total } } }
 *  - exportCsv(params & { type: 'summary' | 'detail' }) → axios blob response
 *  - labels: { users, count, totalAmount, totalValue?, zeroCount?, detailTooltip }
 *    totalValue / zeroCount không truyền thì ẩn cột.
 */
const isFutureMonth = (d) => d.isAfter(dayjs(), "month");
const fmt = (n) => Number(n || 0).toLocaleString("vi-VN");
const monthLabel = (k) => (/^\d{4}-\d{2}$/.test(k) ? `${k.slice(5)}/${k.slice(0, 4)}` : k);

function buildColumns(labels) {
    return [
        { title: "Tháng", dataIndex: "month", render: monthLabel },
        { title: labels.users, dataIndex: "users", align: "right", render: fmt },
        { title: labels.count, dataIndex: "count", align: "right", render: fmt },
        { title: labels.totalAmount, dataIndex: "totalAmount", align: "right", render: (v) => <strong>{fmt(v)}</strong> },
        labels.totalValue && { title: labels.totalValue, dataIndex: "totalValue", align: "right", render: fmt },
        labels.zeroCount && { title: labels.zeroCount, dataIndex: "zeroCount", align: "right", render: fmt },
    ].filter(Boolean);
}

function MonthlyTaxReportModal({ open, onClose, title, filePrefix, note, labels, fetchSummary, exportCsv }) {
    // Mặc định: tháng trước (tháng vừa chốt, hay phải nộp nhất)
    const [range, setRange] = useState(() => [dayjs().subtract(1, "month"), dayjs().subtract(1, "month")]);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);

    const params = range?.[0] && range?.[1] ? { from: range[0].format("YYYY-MM"), to: range[1].format("YYYY-MM") } : null;

    useEffect(() => {
        if (!open || !params) return;
        let cancelled = false;
        setLoading(true);
        fetchSummary(params)
            .then((res) => !cancelled && setData(res.data.data))
            .catch((e) => !cancelled && message.error(e?.response?.data?.message || "Không tải được báo cáo"))
            .finally(() => !cancelled && setLoading(false));
        return () => {
            cancelled = true;
        };
    }, [open, params?.from, params?.to]); // eslint-disable-line react-hooks/exhaustive-deps

    const rows = data ? [...data.months, data.total] : [];

    return (
        <Modal title={title} open={open} onCancel={onClose} footer={null} width={900}>
            <Space wrap className="mb-3">
                {/* 2 ô tháng riêng thay vì RangePicker: RangePicker bắt bấm 2 lần (đầu + cuối),
                    bấm 1 tháng xong nó đứng chờ nên người dùng tưởng nút bị đơ. */}
                <span>Từ tháng</span>
                <DatePicker
                    picker="month"
                    format="MM/YYYY"
                    allowClear={false}
                    value={range[0]}
                    disabledDate={isFutureMonth}
                    // Chọn "từ" sau "đến" thì kéo "đến" theo, xem 1 tháng là bấm đúng 1 lần
                    onChange={(d) => d && setRange(([, to]) => [d, d.isAfter(to, "month") ? d : to])}
                />
                <span>đến</span>
                <DatePicker
                    picker="month"
                    format="MM/YYYY"
                    allowClear={false}
                    value={range[1]}
                    disabledDate={(d) => isFutureMonth(d) || d.isBefore(range[0], "month")}
                    onChange={(d) => d && setRange(([from]) => [from, d])}
                />
                <ExportCsvButton
                    label="Xuất tổng hợp theo tháng"
                    tooltip="Mỗi tháng 1 dòng: số user, số lượt, tổng tiền — dòng cuối là tổng cả kỳ"
                    filename={`${filePrefix}-tong-hop-thang`}
                    disabled={!params}
                    request={() => exportCsv({ ...params, type: "summary" })}
                />
                <ExportCsvButton
                    label="Xuất chi tiết từng lượt"
                    tooltip={labels.detailTooltip}
                    filename={`${filePrefix}-chi-tiet`}
                    disabled={!params}
                    request={() => exportCsv({ ...params, type: "detail" })}
                />
            </Space>
            <Table
                size="small"
                rowKey="month"
                loading={loading}
                columns={buildColumns(labels)}
                dataSource={rows}
                pagination={false}
                rowClassName={(r) => (r.month === "Tổng" ? "font-semibold" : "")}
            />
            <Alert
                className="mt-3"
                type="info"
                showIcon
                message={`Tháng tính theo giờ Việt Nam. Số user ở dòng Tổng là số user KHÁC NHAU cả kỳ (không cộng dồn các tháng). ${note || ""}`}
            />
        </Modal>
    );
}

export default MonthlyTaxReportModal;
